// export default EditorPage;

import { useEffect, useRef, useState } from "react";
import PDFViewer from "../components/PDFViewer";
import DraggableText from "../components/DraggableText";
import EditNavbar from "../components/EditNavbar";
import { replaceText } from "../utils/api";

function EditorPage() {
  const [file, setFile] = useState(null);
  const [originalFileName, setOriginalFileName] = useState("");
  const [textElements, setTextElements] = useState([]);
  const [imageElements, setImageElements] = useState([]);
  const [pageMetrics, setPageMetrics] = useState({});
  const [nextId, setNextId] = useState(1);
  const [nextImageId, setNextImageId] = useState(1);
  const [selectedTextId, setSelectedTextId] = useState(null);
  const [selectedImageId, setSelectedImageId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  const [fontFamily, setFontFamily] = useState("Arial");
  const [fontSize, setFontSize] = useState(16);
  const [fontColor, setFontColor] = useState("#000000");
  const [isBold, setIsBold] = useState(false);
  const [isTransparent, setIsTransparent] = useState(false);

  const dragData = useRef(null);
  const resizeData = useRef(null);

  const API_BASE = "https://smartpdf-bge7.onrender.com/api";

  const revokeImageUrls = (items = imageElements) => {
    items.forEach((item) => {
      if (item?.imageUrl) {
        URL.revokeObjectURL(item.imageUrl);
      }
    });
  };

  useEffect(() => {
    return () => {
      revokeImageUrls(imageElements);
    };
  }, []);
  const [pdfDimensions, setPdfDimensions] = useState({
    width: 0,
    height: 0,
    originalWidth: 0,
    originalHeight: 0,
  });
  const clearSelection = () => {
    setSelectedTextId(null);
    setSelectedImageId(null);
  };

  const resetOverlayState = () => {
    revokeImageUrls(imageElements);
    setTextElements([]);
    setImageElements([]);
    setNextId(1);
    setNextImageId(1);
    setSelectedTextId(null);
    setSelectedImageId(null);
    setPageMetrics({});
    setCurrentPage(1);
  };

  const getMetrics = (pageNumber) => pageMetrics[pageNumber] || null;

  const pdfPointToScreen = (pageNumber, x, y) => {
    const metrics = getMetrics(pageNumber);

    if (!metrics) {
      return { x, y };
    }

    return {
      x: x * (metrics.width / metrics.originalWidth),
      y: y * (metrics.height / metrics.originalHeight),
    };
  };

  const screenPointToPdf = (pageNumber, x, y) => {
    const metrics = getMetrics(pageNumber);

    if (!metrics) {
      return { x, y };
    }

    return {
      x: x * (metrics.originalWidth / metrics.width),
      y: y * (metrics.originalHeight / metrics.height),
    };
  };

  const pdfSizeToScreen = (pageNumber, width, height) => {
    const metrics = getMetrics(pageNumber);

    if (!metrics) {
      return { width, height };
    }

    return {
      width: width * (metrics.width / metrics.originalWidth),
      height: height * (metrics.height / metrics.originalHeight),
    };
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    clearSelection();
  };

  const handlePageLoad = (info) => {
    setPageMetrics((prev) => ({
      ...prev,
      [info.pageNumber]: info,
    }));
  };

  const handleDocumentLoad = ({ numPages }) => {
    setTotalPages(numPages);
  };

  const handlePdfChange = (selectedFile) => {
    if (!selectedFile) return;

    resetOverlayState();
    setFile(selectedFile);
    setOriginalFileName(selectedFile.name);
  };

  const handleAddText = () => {
    const metrics = getMetrics(currentPage);

    const defaultX = metrics ? metrics.originalWidth * 0.18 : 100;
    const defaultY = metrics ? metrics.originalHeight * 0.18 : 100;

    const newTextElement = {
      id: nextId,
      text: "",
      placeholder: "Type here...",
      x: defaultX,
      y: defaultY,
      page: currentPage,
      fontFamily,
      fontSize,
      fontColor,
      isBold,
      isTransparent,
      isFinalized: false,
    };

    setTextElements((prev) => [...prev, newTextElement]);
    setSelectedTextId(nextId);
    setSelectedImageId(null);
    setNextId((prev) => prev + 1);
  };

  const updateTextPositionFromScreen = (id, screenPosition) => {
    const element = textElements.find((item) => item.id === id);
    if (!element) return;

    const pdfPosition = screenPointToPdf(
      element.page,
      screenPosition.x,
      screenPosition.y
    );

    setTextElements((prev) =>
      prev.map((item) =>
        item.id === id
          ? { ...item, x: pdfPosition.x, y: pdfPosition.y }
          : item
      )
    );
  };

  const updateTextContent = (id, newText) => {
    setTextElements((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, text: newText } : item
      )
    );
  };

  const deleteTextElement = (id) => {
    setTextElements((prev) => prev.filter((item) => item.id !== id));
    if (selectedTextId === id) {
      setSelectedTextId(null);
    }
  };

  const finalizeTextElement = (id) => {
    setTextElements((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, isFinalized: true } : item
      )
    );
    setSelectedTextId(null);
  };

  const selectTextElement = (id) => {
    setSelectedTextId(id);
    setSelectedImageId(null);

    const selected = textElements.find((item) => item.id === id);
    if (!selected) return;

    setFontFamily(selected.fontFamily);
    setFontSize(selected.fontSize);
    setFontColor(selected.fontColor);
    setIsBold(selected.isBold);
    setIsTransparent(selected.isTransparent);
  };

  useEffect(() => {
    if (!selectedTextId) return;

    setTextElements((prev) =>
      prev.map((item) =>
        item.id === selectedTextId
          ? {
            ...item,
            fontFamily,
            fontSize,
            fontColor,
            isBold,
            isTransparent,
          }
          : item
      )
    );
  }, [
    selectedTextId,
    fontFamily,
    fontSize,
    fontColor,
    isBold,
    isTransparent,
  ]);

  const parsePagesInput = (input, totalPagesCount) => {
    const pagesSet = new Set();
    const parts = input.split(",").map((part) => part.trim()).filter(Boolean);

    for (const part of parts) {
      if (part.includes("-")) {
        const [startRaw, endRaw] = part.split("-");
        const start = Number(startRaw?.trim());
        const end = Number(endRaw?.trim());

        if (!start || !end || start < 1 || end > totalPagesCount || start > end) {
          throw new Error(`Invalid range: ${part}`);
        }

        for (let page = start; page <= end; page += 1) {
          pagesSet.add(page);
        }
      } else {
        const page = Number(part);

        if (!page || page < 1 || page > totalPagesCount) {
          throw new Error(`Invalid page number: ${part}`);
        }

        pagesSet.add(page);
      }
    }

    return Array.from(pagesSet).sort((a, b) => a - b);
  };

  const handleDelete = async (pageInput) => {
    if (!file) return alert("Please upload a PDF first");
    if (!pageInput?.trim()) return alert("Please enter page numbers");

    let pagesToDelete;

    try {
      pagesToDelete = parsePagesInput(pageInput, totalPages);
    } catch (error) {
      return alert(error.message);
    }

    if (pagesToDelete.length >= totalPages) {
      return alert("You cannot delete all pages");
    }

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("pages", JSON.stringify(pagesToDelete));

      const response = await fetch(`${API_BASE}/delete-pages/`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error(await response.text());
      }

      const blob = await response.blob();
      const nextFile = new File([blob], originalFileName || "edited.pdf", {
        type: "application/pdf",
      });

      resetOverlayState();
      setFile(nextFile);
      alert("Pages deleted successfully!");
    } catch (error) {
      console.error(error);
      alert(`Error deleting pages: ${error.message}`);
    }
  };

  const handleInsert = async (pageNum, insertFile) => {
    if (!file || !insertFile) {
      return alert("Please upload main PDF and choose a PDF to insert");
    }

    const pageNumber = currentPage;

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("insert_file", insertFile);
      formData.append("after_page", pageNumber);

      const response = await fetch(`${API_BASE}/insert-pdf/`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error(await response.text());
      }

      const blob = await response.blob();
      const nextFile = new File([blob], originalFileName || "edited.pdf", {
        type: "application/pdf",
      });

      resetOverlayState();
      setFile(nextFile);
      alert("PDF inserted successfully!");
    } catch (error) {
      console.error(error);
      alert(`Error inserting PDF: ${error.message}`);
    }
  };

  const handleInsertImage = async (pageNum, imageFile) => {
    if (!file || !imageFile) {
      return alert("Please upload PDF and select image");
    }

    const pageNumber = currentPage;
    const metrics = getMetrics(pageNumber);

    const imageUrl = URL.createObjectURL(imageFile);

    const startWidth = 150;
    const startHeight = 150;

    const defaultX = metrics
      ? metrics.originalWidth / 2 - startWidth / 2
      : 100;

    const defaultY = metrics
      ? metrics.originalHeight / 2 - startHeight / 2
      : 100;

    const newImageElement = {
      id: nextImageId,
      imageFile,
      imageUrl,
      x: Math.max(20, defaultX),
      y: Math.max(20, defaultY),
      page: pageNumber,
      width: startWidth,
      height: startHeight,
      isFinalized: false,
    };

    setImageElements((prev) => [...prev, newImageElement]);
    setNextImageId((prev) => prev + 1);
    setSelectedImageId(nextImageId);
    setSelectedTextId(null);
  };
  const selectImageElement = (id) => {
    setSelectedImageId(id);
    setSelectedTextId(null);
  };

  const deleteImageElement = (id) => {
    const imageToDelete = imageElements.find((item) => item.id === id);

    if (imageToDelete?.imageUrl) {
      URL.revokeObjectURL(imageToDelete.imageUrl);
    }

    setImageElements((prev) => prev.filter((item) => item.id !== id));

    if (selectedImageId === id) {
      setSelectedImageId(null);
    }
  };

  const finalizeImageElement = (id) => {
    setImageElements((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, isFinalized: true } : item
      )
    );

    setSelectedImageId(null);
  };

  const startImageDrag = (event, image) => {
    if (image.isFinalized) return;

    event.preventDefault();
    event.stopPropagation();

    const metrics = getMetrics(image.page);
    if (!metrics) return;

    const startMouseX = event.clientX;
    const startMouseY = event.clientY;
    const startX = image.x;
    const startY = image.y;

    const scaleX = metrics.originalWidth / metrics.width;
    const scaleY = metrics.originalHeight / metrics.height;

    const handleMouseMove = (moveEvent) => {
      const dx = (moveEvent.clientX - startMouseX) * scaleX;
      const dy = (moveEvent.clientY - startMouseY) * scaleY;

      setImageElements((prev) =>
        prev.map((item) =>
          item.id === image.id
            ? {
              ...item,
              x: Math.max(0, startX + dx),
              y: Math.max(0, startY + dy),
            }
            : item
        )
      );
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };
  const startImageResize = (event, image) => {
    if (image.isFinalized) return;

    event.preventDefault();
    event.stopPropagation();

    const metrics = getMetrics(image.page);
    if (!metrics) return;

    const startMouseX = event.clientX;
    const startMouseY = event.clientY;
    const startWidth = image.width;
    const startHeight = image.height;

    const scaleX = metrics.originalWidth / metrics.width;
    const scaleY = metrics.originalHeight / metrics.height;

    const handleMouseMove = (moveEvent) => {
      const dx = (moveEvent.clientX - startMouseX) * scaleX;
      const dy = (moveEvent.clientY - startMouseY) * scaleY;

      setImageElements((prev) =>
        prev.map((item) =>
          item.id === image.id
            ? {
              ...item,
              width: Math.max(40, startWidth + dx),
              height: Math.max(40, startHeight + dy),
            }
            : item
        )
      );
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const getLatestTextData = () => {
    return textElements.map((element) => {
      const domNode = document.getElementById(`text-content-${element.id}`);
      const latestText = domNode ? domNode.innerText : element.text;

      return {
        text: latestText,
        x: element.x,
        y: element.y,
        page: element.page - 1,
        fontSize: element.fontSize,
        fontFamily: element.fontFamily,
        fontColor: element.fontColor,
        isBold: element.isBold,
        isTransparent: element.isTransparent,
      };
    });
  };

  const applyAllChangesToPdf = async () => {
    let currentFile = file;

    const textData = getLatestTextData();

    if (textData.length > 0) {
      const textResponse = await replaceText(currentFile, textData);

      if (!textResponse.ok) {
        throw new Error(await textResponse.text());
      }

      const textBlob = await textResponse.blob();
      currentFile = new File([textBlob], "text_applied.pdf", {
        type: "application/pdf",
      });
    }

    for (const image of imageElements) {
      const formData = new FormData();
      formData.append("file", currentFile);
      formData.append("image", image.imageFile);
      formData.append("page", image.page - 1);
      formData.append("x", image.x);
      formData.append("y", image.y);
      formData.append("width", image.width);
      formData.append("height", image.height);

      const imageResponse = await fetch(`${API_BASE}/insert-image/`, {
        method: "POST",
        body: formData,
      });

      if (!imageResponse.ok) {
        throw new Error(await imageResponse.text());
      }

      const imageBlob = await imageResponse.blob();
      currentFile = new File([imageBlob], "image_applied.pdf", {
        type: "application/pdf",
      });
    }

    return currentFile;
  };

  const handlePreview = async () => {
    if (!file) return alert("Please upload a PDF first");

    try {
      const previewFile =
        textElements.length > 0 || imageElements.length > 0
          ? await applyAllChangesToPdf()
          : file;

      const url = URL.createObjectURL(previewFile);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 3000);
    } catch (error) {
      console.error(error);
      alert(`Error generating preview: ${error.message}`);
    }
  };

  const handleSave = async () => {
    if (!file) return alert("Please upload a PDF");

    try {
      const finalFile =
        textElements.length > 0 || imageElements.length > 0
          ? await applyAllChangesToPdf()
          : file;

      const fileName = originalFileName
        ? originalFileName.replace(/\.pdf$/i, "_edited.pdf")
        : "edited.pdf";

      const downloadUrl = URL.createObjectURL(finalFile);

      const anchor = document.createElement("a");
      anchor.href = downloadUrl;
      anchor.download = fileName;
      anchor.click();

      const buffer = await finalFile.arrayBuffer();
      const editedFile = new File([buffer], fileName, {
        type: "application/pdf",
      });

      resetOverlayState();
      setFile(editedFile);

      URL.revokeObjectURL(downloadUrl);
      alert("PDF saved successfully!");
    } catch (error) {
      console.error(error);
      alert(`Error saving PDF: ${error.message}`);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        background: "#f6f8fc",
      }}
    >
      <EditNavbar
        onDelete={handleDelete}
        onInsert={handleInsert}
        onInsertImage={handleInsertImage}
        onPreview={handlePreview}
        onAddText={handleAddText}
        fontFamily={fontFamily}
        setFontFamily={setFontFamily}
        fontSize={fontSize}
        setFontSize={setFontSize}
        fontColor={fontColor}
        setFontColor={setFontColor}
        isBold={isBold}
        setIsBold={setIsBold}
        isTransparent={isTransparent}
        setIsTransparent={setIsTransparent}
        onSave={handleSave}
      />

      <main
        style={{
          flex: 1,
          padding: "20px",
          display: "flex",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        <input
          type="file"
          accept="application/pdf"
          onChange={(event) => handlePdfChange(event.target.files?.[0])}
          style={{
            background: "#fff",
            padding: "10px",
            borderRadius: "10px",
            maxWidth: "360px",
          }}
        />

        {file && (
          <div
            onClick={clearSelection}
            style={{
              minHeight: "650px",
              backgroundColor: "#e5e7eb",
              display: "flex",
              justifyContent: "center",
              alignItems: "flex-start",
              borderRadius: 16,
              overflow: "auto",
              padding: 20,
            }}
          >
            <PDFViewer
              file={file}
              onPageLoad={handlePageLoad}
              onDocumentLoad={handleDocumentLoad}
              onPageChange={handlePageChange}
            >
              {({ pageNumber }) => (
                <>
                  {textElements
                    .filter((item) => item.page === pageNumber)
                    .map((element) => {
                      const screenPosition = pdfPointToScreen(
                        element.page,
                        element.x,
                        element.y
                      );

                      return (
                        <div key={element.id}>
                          {selectedTextId === element.id && (
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                finalizeTextElement(element.id);
                              }}
                              style={{
                                position: "absolute",
                                left: screenPosition.x,
                                top: screenPosition.y - 34,
                                zIndex: 1400,
                                background: "#28a745",
                                color: "#fff",
                                border: "none",
                                borderRadius: 4,
                                padding: "4px 8px",
                                cursor: "pointer",
                              }}
                            >
                              Save
                            </button>
                          )}

                          <DraggableText
                            id={element.id}
                            text={element.text}
                            position={screenPosition}
                            setPosition={updateTextPositionFromScreen}
                            placeholder={element.placeholder}
                            onDelete={deleteTextElement}
                            onSelect={selectTextElement}
                            onTextChange={updateTextContent}
                            isSelected={selectedTextId === element.id}
                            fontFamily={element.fontFamily}
                            fontSize={element.fontSize}
                            fontColor={element.fontColor}
                            isBold={element.isBold}
                            isTransparent={element.isTransparent}
                          />
                        </div>
                      );
                    })}

                  {imageElements
                    .filter((item) => item.page === pageNumber)
                    .map((element) => {
                      const screenPosition = pdfPointToScreen(
                        element.page,
                        element.x,
                        element.y
                      );

                      const screenSize = pdfSizeToScreen(
                        element.page,
                        element.width,
                        element.height
                      );

                      return (
                        <div
                          key={element.id}
                          onMouseDown={(event) => startImageDrag(event, element)}
                          onClick={(event) => {
                            event.stopPropagation();
                            selectImageElement(element.id);
                          }}
                          style={{
                            position: "absolute",
                            left: screenPosition.x,
                            top: screenPosition.y,
                            pointerEvents: "auto",
                            cursor: element.isFinalized ? "default" : "move",
                            zIndex: selectedImageId === element.id ? 1100 : 20,
                            border:
                              selectedImageId === element.id || !element.isFinalized
                                ? "2px solid #007bff"
                                : "none",
                            borderRadius: 4,
                            userSelect: "none",
                          }}
                        >
                          {(selectedImageId === element.id || !element.isFinalized) && (
                            <>
                              <button
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  finalizeImageElement(element.id);
                                }}
                                style={{
                                  position: "absolute",
                                  top: -14,
                                  left: -14,
                                  background: "#28a745",
                                  color: "#fff",
                                  border: "none",
                                  borderRadius: "50%",
                                  width: 28,
                                  height: 28,
                                  cursor: "pointer",
                                  fontSize: 16,
                                  zIndex: 1200,
                                }}
                              >
                                ✓
                              </button>

                              <button
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  deleteImageElement(element.id);
                                }}
                                style={{
                                  position: "absolute",
                                  top: -14,
                                  right: -14,
                                  background: "red",
                                  color: "#fff",
                                  border: "none",
                                  borderRadius: "50%",
                                  width: 28,
                                  height: 28,
                                  cursor: "pointer",
                                  fontSize: 16,
                                  zIndex: 1200,
                                }}
                              >
                                ×
                              </button>

                              <div
                                onMouseDown={(event) => {
                                  event.stopPropagation();
                                  startImageResize(event, element);
                                }}
                                style={{
                                  position: "absolute",
                                  right: -7,
                                  bottom: -7,
                                  width: 14,
                                  height: 14,
                                  background: "#007bff",
                                  border: "2px solid #fff",
                                  cursor: "nwse-resize",
                                  zIndex: 1200,
                                }}
                              />
                            </>
                          )}

                          <img
                            src={element.imageUrl}
                            alt="Inserted"
                            draggable={false}
                            style={{
                              width: screenSize.width,
                              height: screenSize.height,
                              objectFit: "contain",
                              pointerEvents: "none",
                              display: "block",
                            }}
                          />
                        </div>
                      );
                    })}
                </>
              )}
            </PDFViewer>
          </div>
        )}
      </main>
    </div>
  );
}

export default EditorPage;
